import {IStyleParam, wixColorParam} from '@wix/tpa-settings'
import {LIST_LAYOUT, MOBILE_LIST_LAYOUT_TYPE, MOBILE_WIDGET_LAYOUT} from '@wix/wix-events-commons-statics'
import {GetDefaultValueProps, getDefaultFont} from '../../../../../settings-commons/utils/style'
import {widgetStyles} from '../../../stylesParams'
import {GeneratedDeprecatedStyles} from '../../../stylesParams/deprecated-styles'
import {
  isCards,
  isCompactMobile,
  isDailyMobile,
  isFullMobile,
  isGridMobile,
  isList,
  isMobileOnImage,
  isNarrow,
  isOnImage,
  isSideBySide,
} from '../utils'
import {ADDITIONAL_MOBILE_LIST_LAYOUT, DEFAULT_FONT_STYLE, WidgetText} from './constants'
import {WidgetFontDefault, cardsFontDefaults, commonFontDefaults} from './defaults'
import {FontConfig, StudioParamsMap} from './mapper'

interface GetFontConfigParams {
  params: GetDefaultValueProps
  widgetText: WidgetText
  map: StudioParamsMap
}

interface GetWidgetFontDefaultParams extends GetFontConfigParams {
  hover?: boolean
  defaultParam?: IStyleParam
}

export const getFontConfig = ({params, widgetText, map}: GetFontConfigParams): FontConfig => {
  const {getStyleParamValue, isMobile, dimensions} = params
  const width = dimensions?.width
  const listLayout = getStyleParamValue(widgetStyles.listLayout) as LIST_LAYOUT

  if (isNarrow(listLayout, width, isMobile)) {
    return map.mobile[MOBILE_LIST_LAYOUT_TYPE.FULL][widgetText]
  }

  if (isMobile) {
    const mobileLayout = getStyleParamValue(widgetStyles.mobileWidgetLayoutNew) as MOBILE_WIDGET_LAYOUT
    const mobileListLayoutType = getStyleParamValue(widgetStyles.mobileWidgetListLayoutType) as MOBILE_LIST_LAYOUT_TYPE

    return isGridMobile(mobileLayout)
      ? map.mobile[ADDITIONAL_MOBILE_LIST_LAYOUT.GRID][widgetText]
      : isCompactMobile(mobileLayout, mobileListLayoutType)
        ? map.mobile[MOBILE_LIST_LAYOUT_TYPE.STRIPS]?.[widgetText]
        : isFullMobile(mobileLayout, mobileListLayoutType)
          ? map.mobile[MOBILE_LIST_LAYOUT_TYPE.FULL]?.[widgetText]
          : isDailyMobile(mobileLayout, mobileListLayoutType)
            ? map.mobile[MOBILE_LIST_LAYOUT_TYPE.DAILY]?.[widgetText]
            : null
  }

  return map.desktop[listLayout]?.[widgetText]
}

export const getWidgetFontDefault = ({params, widgetText, hover, defaultParam, map}: GetWidgetFontDefaultParams) => {
  const {getStyleParamValue, isMobile} = params
  const listLayout = getStyleParamValue(widgetStyles.listLayout) as LIST_LAYOUT

  const defaults = (isCards(listLayout) ? cardsFontDefaults : commonFontDefaults)[widgetText]
  const defaultFontSize = getDefaultFontSize(defaults, isMobile, hover)

  const {font, size, hoverFont, hoverSize, staticFontSize, hoverStaticFontSize, style} =
    getFontConfig({
      params,
      widgetText,
      map,
    }) ?? {}
  const [fontParam, sizeParam, staticSize] = hover
    ? [hoverFont, hoverSize, hoverStaticFontSize]
    : [font, size, staticFontSize]
  const fontValue = fontParam && getStyleParamValue(fontParam)
  const sizeValue = staticSize ?? (sizeParam && getStyleParamValue(sizeParam))

  if (fontValue) {
    const {value: _value, preset: _preset, ...rest} = fontValue

    return sizeValue
      ? getDefaultFont(defaults.preset, {...rest, size: sizeValue, style: {...rest.style, ...style}})(params)
      : fontValue
  }

  if (hover && defaultParam) {
    const defaultParamValue = getStyleParamValue(defaultParam)
    const {value: _value, preset: _preset, ...rest} = defaultParamValue

    return sizeValue
      ? getDefaultFont(defaults.preset, {...rest, size: sizeValue, style: {...rest.style, ...style}})(params)
      : defaultParamValue
  }

  return getDefaultFont(defaults.preset, {
    size: sizeValue ?? defaultFontSize,
    style: {...DEFAULT_FONT_STYLE, ...style},
  })(params)
}

const getDefaultFontSize = (defaults: WidgetFontDefault, isMobile: boolean, hover?: boolean) => {
  const {fontSize, mobileFontSize, hoverFontSize, hoverMobileFontSize} = defaults

  if (isMobile) {
    return hover ? hoverMobileFontSize : mobileFontSize
  }

  return hover ? hoverFontSize : fontSize
}

export const getWidgetFontColorDefault = ({
  params,
  widgetText,
  hover,
  defaultParam,
  map,
}: GetWidgetFontDefaultParams) => {
  const {getStyleParamValue, isMobile} = params
  const listLayout = getStyleParamValue(widgetStyles.listLayout) as LIST_LAYOUT
  const mobileLayout = getStyleParamValue(widgetStyles.mobileWidgetLayoutNew) as MOBILE_WIDGET_LAYOUT
  const imageEnabled = getStyleParamValue(widgetStyles.listShowImage)
  const onImageLayout = isMobile ? isMobileOnImage(mobileLayout) : isOnImage(listLayout)
  const {color, hoverColor} = getFontConfig({params, widgetText, map}) ?? {}
  const colorParam = hover ? hoverColor : color
  const colorValue = colorParam && getStyleParamValue(colorParam)

  if (colorValue) {
    return colorValue
  }

  if (hover && defaultParam) {
    return getStyleParamValue(defaultParam)
  }

  if (onImageLayout && imageEnabled) {
    return wixColorParam('color-1')(params)
  }

  return wixColorParam('color-5')(params)
}

export const getSocialShareDefault = (params: GetDefaultValueProps, deprecatedStyles: GeneratedDeprecatedStyles) => {
  const {getStyleParamValue, isMobile} = params
  const listLayout = getStyleParamValue(widgetStyles.listLayout) as LIST_LAYOUT
  const mobileLayout = getStyleParamValue(widgetStyles.mobileWidgetLayoutNew) as MOBILE_WIDGET_LAYOUT
  const onImageLayout = isMobile ? isMobileOnImage(mobileLayout) : isOnImage(listLayout)
  const showImage = getStyleParamValue(widgetStyles.listShowImage)

  if (onImageLayout && showImage) {
    return wixColorParam('color-1')(params)
  }
  if (isCards(listLayout) || isSideBySide(listLayout)) {
    return getStyleParamValue(deprecatedStyles.listHeaderTitleColor)
  }
  if (isList(listLayout)) {
    return getStyleParamValue(deprecatedStyles.listContentTextColor)
  }

  return wixColorParam('color-5')(params)
}

interface GetWidgetButtonDefaultFontParams {
  params: GetDefaultValueProps
  fontParam: IStyleParam
  mobileSizeParam: IStyleParam
  mobileCompactSizeParam: IStyleParam
}

export const getWidgetButtonDefaultFont = ({
  params,
  fontParam,
  mobileSizeParam,
  mobileCompactSizeParam,
}: GetWidgetButtonDefaultFontParams) => {
  const {getStyleParamValue, isMobile} = params
  const font = getStyleParamValue(fontParam)
  const mobileSize = getStyleParamValue(mobileSizeParam)
  const mobileCompactSize = getStyleParamValue(mobileCompactSizeParam)

  if (isMobile) {
    const {value: _value, preset: _preset, ...rest} = font
    const mobileLayout = getStyleParamValue(widgetStyles.mobileWidgetLayoutNew) as MOBILE_WIDGET_LAYOUT
    const mobileListLayoutType = getStyleParamValue(widgetStyles.mobileWidgetListLayoutType) as MOBILE_LIST_LAYOUT_TYPE
    const size = isCompactMobile(mobileLayout, mobileListLayoutType) ? mobileCompactSize : mobileSize

    return getDefaultFont('Body-M', {...rest, size})(params)
  }

  return font
}
